/**
 * @fileoverview The shipped extension registry.
 *
 * Foundry's equivalent is "the main bundle's lazy extension-loader array" — the
 * recovered architecture note calls it "the shipped application denominator",
 * which is the right way to think about it: this array is every settings page
 * that EXISTS, and hydration decides which of them a given administrator gets.
 * Nothing else in the Control Panel may enumerate pages.
 *
 * Every entry is lazy. 90 of Foundry's 94 are, and the reason is mechanical
 * rather than stylistic: the Control Panel is one route, so a statically
 * imported registry puts every settings page into the entry chunk whether or
 * not anyone opens the Control Panel.
 *
 * OPERATION NAMES ARE `<service>:<subject>:<verb>`, matching the recovered
 * `control-panel:enrollment:view-role-grants`. They resolve against Cedar.
 */

import recoveredRegistry from "./control-panel-default-registry.json" with { type: "json" };
import type {
  ControlPanelExtensionDefinition,
  ExtensionDisplayGroup,
  ExtensionType,
} from "./ExtensionDefinition";

/** Shorthand for the common case: nothing dynamic, no required services. */
function statically(
  conjunctiveOperations: readonly string[],
): ControlPanelExtensionDefinition["visibility"] {
  return { conjunctiveOperations, disjunctiveOperations: [], requiredServices: [] };
}

const IMPLEMENTED: readonly ControlPanelExtensionDefinition[] = [
  // ── Personal ────────────────────────────────────────────────────────────
  //
  // Type `global`: these configure the signed-in person, who exists in every
  // context, so they must not be filtered out when no organization is selected.
  {
    id: "account",
    displayName: "Account",
    description: "Sign-in identity, agency, and assigned role.",
    type: "global",
    urlPath: "/account",
    displayGroup: "personal",
    keywords: ["identity", "agency", "role", "sign in"],
    supersededBy: null,
    presentation: { isHeaderVisible: true, redirectUrlPaths: [] },
    visibility: statically([]),
    component: () => import("../../../account-extension/src/components/ControlPanelExtension"),
  },
  {
    id: "authentication",
    displayName: "Authentication",
    description: "Passkeys, passwords, and second factors.",
    type: "global",
    urlPath: "/authentication",
    displayGroup: "personal",
    // "SSO" and "MFA" are what someone types; neither appears in the name or
    // the description, and a search box that cannot find the MFA page is worse
    // than no search box. This is exactly what the real `keywords` field is for.
    keywords: ["sso", "mfa", "2fa", "passkey", "password", "saml", "oidc", "identity provider"],
    supersededBy: null,
    presentation: { isHeaderVisible: true, redirectUrlPaths: [] },
    visibility: statically([]),
    component: () => import("../../../authentication-extension/src/components/ControlPanelExtension"),
  },
  {
    id: "authorized-applications",
    displayName: "Authorized applications",
    description: "Third-party applications you've granted access to your account.",
    type: "global",
    urlPath: "/authorized-applications",
    displayGroup: "personal",
    keywords: ["oauth", "consent", "access"],
    supersededBy: null,
    presentation: { isHeaderVisible: true, redirectUrlPaths: [] },
    visibility: statically([]),
    component: () =>
      import("../../../authorized-applications-extension/src/components/ControlPanelExtension"),
  },
  {
    id: "privacy",
    displayName: "Privacy",
    description: "Configure your privacy settings.",
    type: "global",
    urlPath: "/privacy",
    displayGroup: "personal",
    keywords: ["telemetry", "data"],
    supersededBy: null,
    presentation: { isHeaderVisible: true, redirectUrlPaths: [] },
    visibility: statically([]),
    component: () => import("../../../privacy-extension/src/components/ControlPanelExtension"),
  },

  // ── Security and governance ─────────────────────────────────────────────
  {
    id: "roles",
    displayName: "Roles",
    description: "Create and manage roles and their role sets.",
    type: "enrollment",
    urlPath: "/roles",
    displayGroup: "security-governance",
    keywords: ["permissions", "rbac", "grants"],
    supersededBy: null,
    presentation: {
      isHeaderVisible: true,
      redirectUrlPaths: [],
      homePage: {
        title: "Assign roles",
        description: "Grant roles to people to manage aspects of this deployment.",
        icon: "shield",
      },
    },
    visibility: statically(["control-panel:enrollment:view-role-grants"]),
    component: () => import("../../../roles-extension/src/components/ControlPanelExtension"),
  },
  {
    id: "users",
    displayName: "Users",
    description: "Manage user accounts.",
    type: "enrollment",
    urlPath: "/users",
    displayGroup: "security-governance",
    keywords: ["people", "accounts", "principals"],
    supersededBy: null,
    presentation: {
      isHeaderVisible: true,
      redirectUrlPaths: [],
      homePage: {
        title: "Add people",
        description: "Invite people and assign them the roles their work needs.",
        icon: "people",
      },
    },
    visibility: statically(["control-panel:enrollment:view-users"]),
    component: () => import("../../../users-extension/src/components/ControlPanelExtension"),
  },
  {
    id: "groups",
    displayName: "Groups",
    description: "Create and manage groups.",
    type: "enrollment",
    urlPath: "/groups",
    displayGroup: "security-governance",
    keywords: ["teams", "principals", "membership"],
    supersededBy: null,
    presentation: { isHeaderVisible: true, redirectUrlPaths: [] },
    visibility: statically(["control-panel:enrollment:view-groups"]),
    component: () => import("../../../groups-extension/src/components/ControlPanelExtension"),
  },
  {
    id: "markings",
    displayName: "Markings",
    description: "Create, edit, and organize security markings.",
    type: "organization",
    urlPath: "/markings",
    displayGroup: "security-governance",
    keywords: ["classification", "cui", "handling", "caveat"],
    supersededBy: null,
    presentation: {
      isHeaderVisible: true,
      redirectUrlPaths: [],
      homePage: {
        title: "Define markings",
        description: "Set up the classification markings this agency applies to evidence.",
        icon: "tag",
      },
    },
    visibility: statically(["control-panel:organization:view-markings"]),
    component: () => import("../../../markings-extension/src/components/ControlPanelExtension"),
  },
  {
    id: "third-party-applications",
    displayName: "Third-party applications",
    description: "Register OAuth 2.0 clients.",
    type: "enrollment",
    urlPath: "/third-party-applications",
    displayGroup: "security-governance",
    keywords: ["oauth", "clients", "api", "integrations"],
    supersededBy: null,
    presentation: { isHeaderVisible: true, redirectUrlPaths: [] },
    visibility: statically(["control-panel:enrollment:view-clients"]),
    component: () =>
      import("../../../third-party-applications-extension/src/components/ControlPanelExtension"),
  },

  // ── Application configuration ───────────────────────────────────────────
  {
    id: "organizations",
    displayName: "Organizations",
    description: "Organizations scope role sets. The tenant boundary is the agency.",
    type: "enrollment",
    urlPath: "/organizations",
    displayGroup: "application-configuration",
    keywords: ["agency", "tenant", "scope"],
    supersededBy: null,
    presentation: { isHeaderVisible: true, redirectUrlPaths: [] },
    visibility: statically(["control-panel:enrollment:view-organizations"]),
    component: () => import("../../../organizations-extension/src/components/ControlPanelExtension"),
  },

  // ── Monitoring ──────────────────────────────────────────────────────────
  {
    id: "debug",
    displayName: "Debug",
    description: "Inspect diagnostics and performance metrics.",
    type: "global",
    urlPath: "/debug",
    displayGroup: "monitoring",
    keywords: ["diagnostics", "performance", "metrics", "telemetry"],
    supersededBy: null,
    presentation: { isHeaderVisible: true, redirectUrlPaths: [] },
    visibility: statically([]),
    component: () => import("../../../debug-extension/src/components/ControlPanelExtension"),
  },
];


/**
 * The recovered registry, as shipped.
 *
 * 94 definitions from the 6.521.57 bundle, committed beside this file. All
 * Settings rendered 12 rows for a long time because I hand-wrote a 12-entry
 * substitute for this while it sat in the palantir cutover tree — and had even
 * noted that its contract was field-for-field identical to the one I wrote.
 *
 * These arrive WITHOUT a component: they name what the platform can configure,
 * not what we have built. Hydration marks them discoverable-but-locked, which
 * is the case `isVisible`/`isDiscoverable` and the "Show locked settings"
 * toggle were built for.
 */
interface RecoveredDefinition {
  readonly id: string;
  readonly type: string;
  readonly displayName: string;
  readonly description: string;
  readonly urlPath: string;
  readonly displayGroup: string | null;
}

const GROUPS = new Set<string>([
  "aip",
  "application-configuration",
  "communications-support",
  "external-integrations",
  "monitoring",
  "networking",
  "security-governance",
  "user-experience",
  "personal",
]);

const implementedIds = new Set(IMPLEMENTED.map((e) => e.id));

const recovered: readonly ControlPanelExtensionDefinition[] = (
  recoveredRegistry.definitions as readonly RecoveredDefinition[]
)
  /*
    OURS WINS ON A COLLISION. Three ids overlap — authentication, privacy and
    third-party-applications — and for those we have a real page and a real
    description of what OUR version does. Taking theirs would replace a working
    link with a locked row describing Foundry's feature.
  */
  .filter((d) => !implementedIds.has(d.id))
  .map((d) => ({
    id: d.id,
    displayName: d.displayName,
    description: d.description,
    type: d.type as ExtensionType,
    urlPath: d.urlPath,
    displayGroup: (d.displayGroup && GROUPS.has(d.displayGroup)
      ? d.displayGroup
      : undefined) as ExtensionDisplayGroup | undefined,
    keywords: [],
    supersededBy: null,
    presentation: { isHeaderVisible: true, redirectUrlPaths: [] },
    /*
      No operation is required to SEE that a capability exists. Locking these
      behind a permission would hide the platform's shape from exactly the
      administrator whose job is to understand it; what they cannot do is open
      one, and that is enforced by the absent component rather than by a grant.
    */
    visibility: { conjunctiveOperations: [], disjunctiveOperations: [], requiredServices: [] },
  }));

/**
 * Everything the Control Panel lists: what we have built, then what the
 * platform defines. Order within a group is by display name, applied during
 * hydration, so the concatenation order here does not leak into the page.
 */
export const DEFAULT_EXTENSION_REGISTRY: readonly ControlPanelExtensionDefinition[] = [
  ...IMPLEMENTED,
  ...recovered,
];
